import React, { useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import { Doughnut, Line } from "react-chartjs-2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { appleApi } from "../../../services/api";
import { FaChartBar, FaSearch, FaFileDownload } from "react-icons/fa";
import CountryList from "../../CountryList";

const AnalyticsAndReporting = () => {
  const [appName, setAppName] = useState("");
  const [country, setCountry] = useState("");
  const [appDetails, setAppDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchAppDetails = async () => {
    try {
      setLoading(true);
      const { data: result } = await appleApi.get("analytics-and-reporting/", {
                                params: {
                                  app_name: appName,
                                  country_code: country,
                                },
                              });
      setAppDetails(result ?? {});
      setError(null);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching app details:", error);
      setError("Failed to fetch app details");
      setAppDetails({});
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchAppDetails();
  };

  const downloadCSV = () => {
    if (!appDetails) return;
    const csvContent = `data:text/csv;charset=utf-8,${[
      "Total Installs,Estimated Uninstall Rate (%),Average Rating,Total Reviews,Daily Active Users,Monthly Active Users,Retention Rate (%),Churn Rate (%),Avg Session Duration,User Acquisition Cost,Lifetime Value,Average Revenue Per User,Total Reviews (KPI),Average Score",
      `${appDetails["Total Installs"]},${appDetails["Estimated Uninstall Rate (rough)"].toFixed(2)},${appDetails["Average Rating"].toFixed(2)},${appDetails["Total Reviews"].toLocaleString()},${appDetails.DAU},${appDetails.MAU},${appDetails.retention_rate},${appDetails.churn_rate},${appDetails.avg_session_duration},${appDetails.UAC},${appDetails.LTV},${appDetails.ARPU},${appDetails.userRatingCount},${appDetails.averageUserRating}`,
    ].join("\n")}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "analytics_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDF = async () => {
    if (!appDetails) return;
  
    // Fallback values in case the fields are missing or undefined
    const totalInstalls = appDetails["estimated_installs"] || 0;
    const uninstallRateYearly = appDetails["uninstall_rate_yearly"] ? appDetails["uninstall_rate_yearly"].toFixed(2) : "N/A";
    const uninstallRateMonthly = appDetails["uninstall_rate_monthly"] ? appDetails["uninstall_rate_monthly"].toFixed(2) : "N/A";
    const averageRating = appDetails["averageUserRating"] ? appDetails["averageUserRating"].toFixed(2) : "N/A";
    const totalReviews = appDetails["userRatingCount"] ? appDetails["userRatingCount"].toLocaleString() : "N/A";
    const doc = new jsPDF();
    doc.text("Analytics Report", 14, 16);
  
    // Ensure the table is added properly
    autoTable(doc, {
      head: [
        ["Total Installs", "Estimated Uninstall Rate (%)", "Average Rating", "Total Reviews"],
      ],
      body: [
        [totalInstalls, uninstallRateYearly, uninstallRateMonthly, averageRating, totalReviews],
      ],
      startY: 30, // Position of the table on the page
      theme: 'grid', // Set theme for the table
      styles: { fontSize: 10 }, // Adjust font size
    });
  
    const chartContainer1 = document.querySelector("#uninstallRateChart");
    const chartContainer2 = document.querySelector("#averageRatingChart");
  
    if (chartContainer1 && chartContainer2) {
      const canvas1 = await html2canvas(chartContainer1);
      const imgData1 = canvas1.toDataURL("image/png");
      const canvas2 = await html2canvas(chartContainer2);
      const imgData2 = canvas2.toDataURL("image/png");
  
      const imgWidth = 180;
      const imgHeight1 = (canvas1.height * imgWidth) / canvas1.width;
      const imgHeight2 = (canvas2.height * imgWidth) / canvas2.width;
  
      doc.addPage();
      doc.text("Uninstall Rate Chart", 14, 16);
      doc.addImage(imgData1, "PNG", 14, 30, imgWidth, imgHeight1);
  
      doc.addPage();
      doc.text("Average Rating Chart", 14, 16);
      doc.addImage(imgData2, "PNG", 14, 30, imgWidth, imgHeight2);
    }
  
    doc.save("analytics_report.pdf");
  };
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <div className="flex items-center mb-6">
          <FaChartBar className="text-blue-600 text-2xl mr-3" />
          <h2 className="text-2xl font-bold text-gray-800">Analytics & Reporting</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Name
              </label>
              <input
                type="text"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                id="appName"
                placeholder="Enter Your App Name"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                required
              />
            </div>

            <div className="md:col-span-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <CountryList
                onSelect={(selectedCountry) => setCountry(selectedCountry)}
              />
            </div>

            <div className="md:col-span-2 flex items-end">
              <button
                type="submit"
                className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="mr-2" />
                    Loading...
                  </>
                ) : (
                  <>
                    <FaSearch className="mr-2" /> Search
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {loading && (
        <div className="flex justify-center items-center h-64">
          <Spinner animation="border" role="status" className="text-blue-600" />
          <span className="ml-3 text-lg text-gray-600 font-medium">Loading... Please Wait</span>
        </div>
      )}
      {error && <div className="alert alert-danger mt-3">{error}</div>}

      {appDetails && (
        <div className="mt-8">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Analytics Data</h3>
            <div className="overflow-x-auto">
              <Table striped bordered hover responsive className="mb-0">
                <thead className="bg-gray-50">
                  <tr>
                    <th>Total Installs</th>
                    <th>Estimated Uninstall Rate Yearly (%)</th>
                    <th>Estimated Uninstall Rate Monthly (%)</th>
                    <th>Average Rating</th>
                    <th>Total Reviews</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{appDetails.estimated_installs}</td>
                    <td>{appDetails.uninstall_rate_yearly}%</td>
                    <td>{appDetails.uninstall_rate_monthly}%</td>
                    <td>{appDetails.averageUserRating}</td>
                    <td>{appDetails.userRatingCount}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h5 className="text-center font-semibold mb-4">Uninstall Rate</h5>
              <div className="h-[400px] relative">
                <Doughnut
                  id="uninstallRateChart"
                  data={{
                    labels: ["Uninstalled", "Remaining"],
                    datasets: [
                      {
                        data: [
                          appDetails["uninstall_rate_yearly"],
                          100 - appDetails["uninstall_rate_monthly"],
                        ],
                        backgroundColor: ["#FF6384", "#36A2EB"],
                        hoverBackgroundColor: ["#FF6384", "#36A2EB"],
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top",
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h5 className="text-center font-semibold mb-4">Rating Trends</h5>
              <div className="h-[400px] relative">
                <Line
                  id="averageRatingChart"
                  data={{
                    labels: appDetails.ratingPhases || ["Phase 1", "Phase 2", "Phase 3", "Latest"], // Default phases
                    datasets: [
                      {
                        label: "Average Rating",
                        data: appDetails.ratingData || [4.2, 4.3, 4.5, appDetails["averageUserRating"]],
                        fill: false,
                        borderColor: "#4BC0C0",
                        tension: 0.4,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                        position: "bottom",
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* Download Buttons */}
          <div className="flex gap-4 justify-end">
            <button className="flex items-center bg-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors" onClick={downloadCSV}>
              <FaFileDownload className="mr-2" /> Download CSV
            </button>
            <button className="flex items-center bg-red-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors" onClick={downloadPDF}>
              <FaFileDownload className="mr-2" /> Download PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsAndReporting;
